import { useEffect, useRef, useState, useCallback, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import "./TreeView.scss";
import { useCanvasInteractions } from "./canvasInteractions/useCanvasInteractions";
import { getFullNodePositions, NODE_WIDTH, NODE_HEIGHT, type DrawNodeTranslations } from "./drawNode/drawNode";
import defaultContent from "../../content/treeView";
import { FetchTreeViewData } from "./TreeViewDataLayer";
import type { TreeNodeData, TreeViewParams } from "./TreeViewDataLayer";
import { store as accBootStore } from "../Lit/acc-boot/acc-boot.js";
import { useAccBootStore } from "../../common/hooks/useAccBootStore";
import { useAppSelector, useAppDispatch } from "../../store";
import { clearTreeViewState } from "../../reducers/treeViewUiState";
import { TreeViewNodeModal } from "./TreeViewNodeModal/TreeViewNodeModal";
import {
  useCanvasSetup,
  useCanvasRendering,
  useFitToView,
  useFullscreen,
  useFullscreenRefit,
  useInternationalFiltering,
  useTreeViewExternalLinks,
  useTreeViewNavigation,
  useTreeViewNodeState,
  useTreeViewSearch,
  useTreeViewWatchlistToggle,
  useVolumeFiltering,
} from "./hooks";
import { useWatchlistFetch } from "../ActionReports/hooks/useWatchlistFetch";
import { buildWatchlistSet, makeWatchlistKey } from "./watchlistUtils";
import { TreeViewFullscreenControls } from "./TreeViewFullscreenControls/TreeViewFullscreenControls";
import type { LosUiHeaderFilterFlags } from "../RwLos/LosToolbar/LosFilterModal/LosFilterModal";
import { TreeViewZoomControls } from "./TreeViewZoomControls/TreeViewZoomControls";
import { TreeViewFooter } from "./TreeViewFooter/TreeViewFooter";

export const LOS_GRID_CONFIG = Object.freeze({
  UI_MENU_SHOW_PERFHISTORY: "uiMenuShowPerfHistory",
  UI_MENU_SHOW_PROFILE: "uiMenuShowProfile",
  UI_MENU_SHOW_ISOLATE: "uiMenuShowIsolate",
  UI_MENU_SHOW_WATCHLIST: "uiMenuShowWatchlist",
  UI_MENU_SHOW_EXPANDGROUP: "uiMenuShowExpandGroup",
  STRING_AWARD_PLATINUM_OR_ABOVE: "stringAwardPlatinumOrAbove",
  STRING_AWARD_ASSOCIATE_BELOW_SILVER: "stringAwardAssociateBelowSilver",
  STRING_BADGE_ABO: "stringBadgeAbo",
  DATA_SHOW_INACTIVEABOS: "dataShowInactiveAbos",
  DATA_SHOW_KPIS: "dataShowKpis",
  DATA_SHOW_ZEROVOLUMEABOS: "dataShowZeroVolumeAbos",
  DATA_SHOW_CUSTOMER_MEMBERS: "dataShowCustomerMembers",
  DATA_SHOW_INTERNATIONAL_ABOS: "dataShowInternationalAbos",
});

export type LosGridConfigKey = keyof typeof LOS_GRID_CONFIG;
export type LosGridConfigValue = (typeof LOS_GRID_CONFIG)[LosGridConfigKey];
export type TreeViewConfig = Partial<Record<LosGridConfigValue, boolean>>;

export interface ExternalLink {
  name: string;
  label: string;
  link: string;
}

export interface TreeViewExternalLinksConfig {
  externalLinks?: ExternalLink[];
}

export interface IsolatedAbo {
  aff: string;
  abo: string;
  /** The ABO's true level (1-indexed) in the full, pre-isolation tree. A
   *  fresh fetch rooted at this ABO always reports it as level 1, so this is
   *  needed to correct the displayed level while isolated. */
  level: number;
}

interface TreeViewProps {
  callData?: boolean;
  period?: string;
  params?: Partial<TreeViewParams>;
  highlightPredicate?: (node: TreeNodeData) => boolean;
  resetExpandedOnNewData?: boolean;
  filterLevel2International?: boolean;
  initialData?: TreeNodeData | null;
  config?: TreeViewConfig;
  externalLinks?: ExternalLink[];
  scrollRef?: React.RefObject<HTMLElement | null>;
  contextKey?: string;
  isolatedAbo?: IsolatedAbo | null;
  onIsolateChange?: (abo: TreeNodeData | null) => void;
  /** Fired whenever a node's expanded state changes via the tree map's own UI (the
   *  Expand Group action) - lets a host mirror the change into a differently-rendered
   *  sibling view (e.g. List View's Lit grid) that has no shared state store. */
  onExpandChange?: (abo: { aff: number; abo: number }, expanded: boolean) => void;
  /** Fired once, synchronously, right before a bulk expand/collapse action (Expand All,
   *  Collapse All, or a recursive Expand Group) starts emitting its onExpandChange calls -
   *  lets a host with its own queue of those calls (e.g. RwLos mirroring into the Lit grid)
   *  invalidate/abort anything still queued from a previous bulk action before this one's
   *  own calls are enqueued behind it. Without this, clicking Collapse All right after
   *  Expand All queues the collapse behind however many expand calls are still draining,
   *  so the grid keeps visibly re-expanding nodes after the user already asked to collapse.
   *  Receives the full list of nodes this batch is about to change, so a host can also
   *  kick off any of its own up-front work for the whole batch (e.g. RwLos pre-fetching
   *  List View's lazily-loaded children for every target in parallel) before draining
   *  its per-node queue one at a time. */
  onExpandBatchStart?: (changes: Array<{ aff: number; abo: number; expanded: boolean }>) => void;
  /** ABO number to restore the search selection/highlight for once the tree loads -
   *  e.g. a search made in list view before switching to this view. */
  initialSelectedAboNumber?: string | null;
  /** Bumped by the parent whenever the user clears the LOS search box - resets the
   *  current search selection/highlight and closes the node modal if open. A plain
   *  counter (not a boolean) so every clear re-fires the effect even if the previous
   *  selection was already null. */
  clearSelectionToken?: number;
  enableFullscreen?: boolean;
  enableInternationalToggle?: boolean;
  /** Market uiHeader filter flags — gates which international toggles the fullscreen filter shows.
   *  When omitted, all international toggles are shown (existing behaviour). */
  uiHeader?: LosUiHeaderFilterFlags;
  /** Extra controls rendered in the fullscreen toolbar below the search/international
   *  row — e.g. Who's New's month-range tabs. Report-specific, so it's left as a
   *  freeform slot rather than growing TreeView's prop list with each new report's needs. */
  fullscreenExtraControls?: ReactNode;
}

const EMPTY_PARAMS: Partial<TreeViewParams> = {};

// After a pinch-zoom gesture ends, snap to the nearest node only if the empty gap between
// the view center and that node's edge exceeds this fraction of the viewport's smaller
// dimension — small gaps aren't worth an unexpected pan correction.
const GAP_SNAP_THRESHOLD_RATIO = 0.15;

const DEFAULT_CONFIG: TreeViewConfig = {
  [LOS_GRID_CONFIG.UI_MENU_SHOW_PROFILE]: true,
  [LOS_GRID_CONFIG.UI_MENU_SHOW_WATCHLIST]: true,
  [LOS_GRID_CONFIG.UI_MENU_SHOW_EXPANDGROUP]: true,
  [LOS_GRID_CONFIG.UI_MENU_SHOW_PERFHISTORY]: false,
  [LOS_GRID_CONFIG.UI_MENU_SHOW_ISOLATE]: false,
  [LOS_GRID_CONFIG.DATA_SHOW_INACTIVEABOS]: true,
  [LOS_GRID_CONFIG.DATA_SHOW_KPIS]: true,
  [LOS_GRID_CONFIG.DATA_SHOW_ZEROVOLUMEABOS]: true,
  [LOS_GRID_CONFIG.DATA_SHOW_CUSTOMER_MEMBERS]: false,
  [LOS_GRID_CONFIG.DATA_SHOW_INTERNATIONAL_ABOS]: true,
};

const TreeView = ({
  callData = false,
  period = "",
  params = EMPTY_PARAMS,
  highlightPredicate,
  resetExpandedOnNewData = false,
  filterLevel2International = false,
  initialData: propInitialData,
  config: propConfig,
  externalLinks: propExternalLinks = [],
  scrollRef,
  contextKey = "default",
  isolatedAbo,
  onIsolateChange,
  onExpandChange,
  onExpandBatchStart,
  initialSelectedAboNumber,
  clearSelectionToken,
  enableFullscreen = false,
  enableInternationalToggle = true,
  uiHeader,
  fullscreenExtraControls,
}: TreeViewProps) => {
  const { t } = useTranslation(["treeView", "los"]);
  const [pendingPanToNodeId, setPendingPanToNodeId] = useState<string | null>(null);
  // Shared with List View's LosFilterModal ("Show Data" / "Show ABOs With Zero Volume")
  // and TreeView's own fullscreen TreeViewInternationalFilter panel via the acc-boot store,
  // so toggling either one stays in sync regardless of which view is active.
  const { state: accBootState, setStoreState } = useAccBootStore();
  const hideVolume = accBootState.hideVolume;
  const hideZeroVolumeAbos = accBootState.hideABOsZeroVolume;

  const config = useMemo(() => ({ ...DEFAULT_CONFIG, ...propConfig }), [propConfig]);

  const fetchParams = useMemo(
    () => (isolatedAbo ? { ...params, Aff: isolatedAbo.aff, Abo: isolatedAbo.abo } : params),
    [params, isolatedAbo],
  );

  const configExternalLinks = useTreeViewExternalLinks();
  const externalLinks = useMemo(
    () => [...configExternalLinks, ...propExternalLinks],
    [configExternalLinks, propExternalLinks],
  );

  const translations: DrawNodeTranslations = useMemo(
    () => ({
      confidentialUser: t("confidentialUser", defaultContent.confidentialUser),
      pabo: t("paboBadge", defaultContent.paboBadge),
      ppv: t("personalPV", defaultContent.personalPV),
      gpv: t("groupPV", defaultContent.groupPV),
      bonusPercent: t("percent", defaultContent.percent),
    }),
    [t],
  );

  const dispatch = useAppDispatch();
  const { isLoading, treeViewData, errorStatus } = FetchTreeViewData(period, !callData, fetchParams);
  const { toggleWatchlist, isInWatchlist } = useTreeViewWatchlistToggle();
  useWatchlistFetch();

  const { watchlistItems } = useAppSelector((state) => state.watchlist);
  const cachedExpanded = useAppSelector((state) => state.treeViewUiState.byContext[contextKey]?.expandedNodes);
  const hasSeededRef = useRef(false);
  // Read via a ref (rather than depending on cachedExpanded directly below) so the tree
  // rebuild effect doesn't re-fire every time the user's own expand/collapse actions
  // dispatch back into Redux - otherwise that second pass re-seeds expanded state purely
  // from the cache, which only contains explicitly-toggled nodes (not e.g. the root's
  // default-expanded state), collapsing everything but the just-toggled node.
  const cachedExpandedRef = useRef(cachedExpanded);
  useEffect(() => {
    cachedExpandedRef.current = cachedExpanded;
  }, [cachedExpanded]);

  const prevPeriodRef = useRef(period);
  const periodChangedRef = useRef(false);
  useEffect(() => {
    if (period !== prevPeriodRef.current) {
      prevPeriodRef.current = period;
      hasSeededRef.current = false;
      periodChangedRef.current = true;
      dispatch(clearTreeViewState({ contextKey }));
    }
  }, [period, contextKey, dispatch]);

  // Isolating (or un-isolating) switches the tree to a different subtree/root -
  // any expanded state cached for the previous context no longer applies and
  // must not leak into the newly isolated (or restored) view.
  const isolatedAboKey = isolatedAbo ? `${isolatedAbo.aff}-${isolatedAbo.abo}` : null;
  const prevIsolatedAboKeyRef = useRef(isolatedAboKey);
  useEffect(() => {
    if (isolatedAboKey !== prevIsolatedAboKeyRef.current) {
      prevIsolatedAboKeyRef.current = isolatedAboKey;
      hasSeededRef.current = false;
      periodChangedRef.current = true;
      dispatch(clearTreeViewState({ contextKey }));
    }
  }, [isolatedAboKey, contextKey, dispatch]);

  const { canvasRef, containerRef, canvasSize, paperInitializedRef } = useCanvasSetup();

  const { filterData, inInternational, hideInternationalDownlines } = useInternationalFiltering({
    // filterLevel2International targets the true root's frontline (level 2 relative to
    // the full tree). A fresh isolate fetch re-roots the tree, so the isolated ABO's own
    // children become level 2 too - applying this filter while isolated wrongly strips
    // the isolated ABO's international frontline downlines.
    filterLevel2International: filterLevel2International && !isolatedAboKey,
  });

  // Switching between International and In-Market view swaps to a structurally
  // different filtered node set, so a node expanded under one no longer corresponds
  // to the same visible node under the other - its cached expanded state must not
  // carry over. This mirrors the period/isolatedAboKey resets above, but previously
  // had no equivalent: nothing actually cleared the Redux cache here, it only looked
  // reset because the reshaped node set broke mergeWatchlistFlags's identity match
  // against the previous tree and fell back to default expanded flags - the stale
  // cache remained underneath and resurfaced the moment TreeView next remounted with
  // hasSeededRef reset (e.g. after bouncing through List View) (ABOT-13141).
  const prevInInternationalRef = useRef(inInternational);
  useEffect(() => {
    if (inInternational !== prevInInternationalRef.current) {
      prevInInternationalRef.current = inInternational;
      hasSeededRef.current = false;
      periodChangedRef.current = true;
      dispatch(clearTreeViewState({ contextKey }));
    }
  }, [inInternational, contextKey, dispatch]);

  const { filterZeroVolumeData } = useVolumeFiltering({ hideZeroVolumeAbos });

  const initialData = useMemo(() => {
    return propInitialData ?? treeViewData.treeData ?? null;
  }, [propInitialData, treeViewData.treeData]);
  // The old isolated payload remains available while the full tree refetches after
  // Undo Downline LOS. Preserve the explicit collapse until that payload is replaced.
  const pendingUnisolateRootKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const pendingRootKey = pendingUnisolateRootKeyRef.current;
    const loadedRootKey = initialData ? `${initialData.aff}-${initialData.abo}` : null;
    if (!isolatedAboKey && pendingRootKey && loadedRootKey && loadedRootKey !== pendingRootKey) {
      pendingUnisolateRootKeyRef.current = null;
    }
  }, [isolatedAboKey, initialData]);

  // Isolating an in-market ABO whose downlines are international otherwise leaves
  // the tree map empty under the default "hide international downlines" filter,
  // even though List View shows them (its own isolate path has no such filter) -
  // auto-disable the filter for the isolate session so both views agree.
  // Only auto-*enable* it back on un-isolate if this effect is the one that turned
  // it off, so a user's own explicit toggle while isolated is never overridden.
  const autoDisabledHideInternationalRef = useRef(false);
  useEffect(() => {
    if (!isolatedAboKey) {
      if (autoDisabledHideInternationalRef.current) {
        autoDisabledHideInternationalRef.current = false;
        accBootStore.setState({ hideInternationalDownlines: true });
      }
      return;
    }
    if (hideInternationalDownlines && initialData?.downlines?.some((child) => child.internationalFlag)) {
      autoDisabledHideInternationalRef.current = true;
      accBootStore.setState({ hideInternationalDownlines: false });
    }
  }, [isolatedAboKey, initialData, hideInternationalDownlines]);

  const [aboData, setAboData] = useState<TreeNodeData | null>(initialData);
  const prevInitialDataRef = useRef<TreeNodeData | null>(null);

  useEffect(() => {
    const filtered = filterZeroVolumeData(filterData(initialData));
    if (!filtered) {
      setAboData(filtered);
      return;
    }

    const watchlistSet = buildWatchlistSet(watchlistItems);

    // Reset expanded state when initialData changes if resetExpandedOnNewData is set (e.g. renewals tab switch).
    // This ensures the tree structure rebuilds from scratch rather than preserving stale expanded state.
    const isNewData = resetExpandedOnNewData && initialData !== prevInitialDataRef.current;
    prevInitialDataRef.current = initialData;

    setAboData((prev) => {
      // Build a flat identity map from the current tree so expanded state can be looked up by
      // abo+aff key. This is necessary because filtering can add or remove nodes from a
      // downlines array, shifting indices and causing index-based lookups to match the wrong node.
      const buildNodeMap = (node: TreeNodeData | null, map: Map<string, TreeNodeData> = new Map()) => {
        if (!node) return map;
        map.set(`${node.aff}-${node.abo}`, node);
        node.downlines?.forEach((child) => buildNodeMap(child, map));
        return map;
      };
      const prevNodeMap = buildNodeMap(prev ?? null);

      // Merge watchlist flags while preserving user-modified expanded states from current aboData.
      // Skip preserving expanded state when new tree data arrives for renewal tab changes.
      // Also stamp whosNewAbo from the highlightPredicate so drawNode can render the gold border
      // without depending on the backend to set this flag.
      // On first mount, seed expanded state from Redux cache so the tree restores its previous
      // expanded state after the component unmounts and remounts (e.g. toggling list/tree view).
      // A List View collapse-to-frontline (or root Collapse Group) writes an empty
      // expandedNodes array, not no array - checking .length > 0 treated that real "collapse
      // everything" cache the same as "never cached at all" and fell through to each node's
      // own stale expanded flag instead, so isExpandedBeyondFrontline (and both the toolbar's
      // and the modal's Expand/Collapse Group labels, which derive from it) stayed stuck on
      // the pre-collapse state until a second click forced a real recompute (ABOT-13129).
      const cachedExpandedValue = cachedExpandedRef.current;
      const cachedSet =
        !hasSeededRef.current && cachedExpandedValue !== undefined ? new Set(cachedExpandedValue) : null;

      const isPeriodChange = periodChangedRef.current;
      periodChangedRef.current = false;

      const mergeWatchlistFlags = (sourceNode: TreeNodeData, isRoot = false): TreeNodeData => {
        const currentNode = prevNodeMap.get(`${sourceNode.aff}-${sourceNode.abo}`);
        const key = `${sourceNode.aff}-${sourceNode.abo}`;
        const fallbackExpanded = isRoot ? true : sourceNode.expanded;
        // Root's own key is never written to the Redux cache - expandAll/toggleExpand
        // only push a change entry when a node's expanded state actually flips, and
        // root already defaults to expanded on first load, so it never flips there.
        // Falling through to cachedSet.has(key) for root would always miss and force
        // it closed (collapsing the whole tree) any time the cache is consulted after
        // root's children get expanded some other way - so root always resolves via
        // its live/fallback state instead, regardless of what's cached.
        // A Downline LOS/isolate fetch roots the new tree at the selected ABO.
        // Its immediate downlines are the first visible row, so keep that root
        // expanded even though the fresh API payload marks it collapsed.
        // Undo uses the prior isolated response until the full tree arrives. That
        // response still marks its root expanded, so preserve the user's collapse
        // rather than reopening its direct downlines during the refetch.
        const isPendingUnisolateRoot = isRoot && pendingUnisolateRootKeyRef.current === key;
        const expanded = isPendingUnisolateRoot
          ? (currentNode?.expanded ?? sourceNode.expanded)
          : isRoot && isolatedAboKey
            ? true
            : isNewData || isPeriodChange
              ? sourceNode.expanded
              : isRoot
                ? (currentNode?.expanded ?? fallbackExpanded)
                : cachedSet
                  ? cachedSet.has(key)
                  : (currentNode?.expanded ?? fallbackExpanded);
        return {
          ...sourceNode,
          expanded,
          watchlist: watchlistSet.has(makeWatchlistKey(sourceNode.aff, sourceNode.abo)),
          whosNewAbo: highlightPredicate ? highlightPredicate(sourceNode) : false,
          downlines: sourceNode.downlines?.map((child) => mergeWatchlistFlags(child)),
        };
      };

      const merged = mergeWatchlistFlags(filtered, true);
      if (cachedSet) hasSeededRef.current = true;
      return merged;
    });
  }, [
    initialData,
    filterData,
    filterZeroVolumeData,
    watchlistItems,
    resetExpandedOnNewData,
    highlightPredicate,
    isolatedAboKey,
  ]);

  const contentBounds = useMemo(() => {
    if (!aboData || canvasSize.width === 0) return null;

    const positions = getFullNodePositions(aboData, canvasSize.width / 2, 80);
    if (positions.size === 0) return null;

    const xs = [...positions.values()].map((p) => p.x);
    const ys = [...positions.values()].map((p) => p.y);

    return {
      minX: Math.min(...xs) - NODE_WIDTH / 2,
      maxX: Math.max(...xs) + NODE_WIDTH / 2,
      minY: Math.min(...ys) - NODE_HEIGHT / 2,
      maxY: Math.max(...ys) + NODE_HEIGHT / 2,
    };
  }, [aboData, canvasSize.width]);

  // When a pinch-zoom gesture ends with the view centered on empty space (a gap between
  // nodes rather than on a node), nudge the pan so the nearest node is centered instead.
  // Only kicks in once the gap is large on screen — small gaps aren't worth correcting.
  const getSnapPan = useCallback(
    (currentPan: { x: number; y: number }, currentZoom: number) => {
      if (!aboData || canvasSize.width === 0 || canvasSize.height === 0) return null;

      const positions = getFullNodePositions(aboData, canvasSize.width / 2, 80);
      if (positions.size === 0) return null;

      const centerX = canvasSize.width / 2 - currentPan.x;
      const centerY = 80 - currentPan.y;

      const allPositions = [...positions.values()];
      const nearest = allPositions.reduce((closest, pos) => {
        const dist = Math.hypot(pos.x - centerX, pos.y - centerY);
        const closestDist = Math.hypot(closest.x - centerX, closest.y - centerY);
        return dist < closestDist ? pos : closest;
      });

      const gapX = Math.max(0, Math.abs(nearest.x - centerX) - NODE_WIDTH / 2);
      const gapY = Math.max(0, Math.abs(nearest.y - centerY) - NODE_HEIGHT / 2);
      const screenGap = Math.hypot(gapX, gapY) * currentZoom;

      const viewportMinDimension = Math.min(canvasSize.width, canvasSize.height);
      if (screenGap < viewportMinDimension * GAP_SNAP_THRESHOLD_RATIO) return null;

      return {
        x: canvasSize.width / 2 - nearest.x,
        y: 80 - nearest.y,
      };
    },
    [aboData, canvasSize],
  );

  const {
    pan,
    setPan,
    zoom,
    setZoom,
    isDragging,
    mouseDownOnCanvas,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleWheelZoom,
    resetPan,
    resetZoom,
  } = useCanvasInteractions({ canvasSize, contentBounds, canvasRef, getSnapPan });

  // Only flags that change the visible node set (not hideVolume, which just hides KPI
  // values inside cards) should trigger a re-fit.
  const filterSignature = `${inInternational}-${hideInternationalDownlines}-${hideZeroVolumeAbos}`;
  const treeViewRootRef = useRef<HTMLDivElement>(null);

  const { isReady: isFitReady, resetFit } = useFitToView({
    aboData,
    canvasSize,
    setZoom,
    setPan,
    setAboData,
    treeViewRootRef,
    highlightPredicate,
    filterSignature,
  });

  const { isFullscreen, toggleFullscreen } = useFullscreen();
  useFullscreenRefit({ isFullscreen, canvasSize, resetFit });

  const { panToNode, expandPathToNode } = useTreeViewNavigation({
    aboData,
    canvasSize,
    zoom,
    setZoom,
    setPan,
    setAboData,
    pendingPanToNodeId,
    setPendingPanToNodeId,
  });

  const {
    selectedAbo,
    setSelectedAbo,
    selectedNodeId,
    setSelectedNodeId,
    handleNodeSelect,
    toggleExpand,
    expandAll,
    collapseToFrontline,
    isExpandedBeyondFrontline,
    canSelectedAboExpand,
    canSelectedAboIsolate,
    isSelectedAboExpanded,
    selectedAboLevel,
    updateNodeWatchlist,
  } = useTreeViewNodeState({
    aboData,
    setAboData,
    panToNode,
    setPendingPanToNodeId,
    inInternational,
    contextKey,
    rootLevel: isolatedAbo?.level ?? 1,
  });

  // Clears the current search selection/highlight (and closes the node modal, since
  // it's keyed off selectedAbo) when the user clears the LOS search box - skips the
  // initial undefined/mount value so mounting doesn't spuriously clear anything.
  const prevClearSelectionTokenRef = useRef(clearSelectionToken);
  useEffect(() => {
    if (clearSelectionToken === undefined || clearSelectionToken === prevClearSelectionTokenRef.current) return;
    prevClearSelectionTokenRef.current = clearSelectionToken;
    setSelectedAbo(null);
    setSelectedNodeId(null);
  }, [clearSelectionToken, setSelectedAbo, setSelectedNodeId]);

  // Mirror the tree map's own Expand Group action out to a host-provided sync (e.g.
  // List View), which has no visibility into TreeView's internal expand state. Uses
  // toggleExpand's own per-node change list rather than just the clicked node, since
  // expanding the root toggles its children (not the root itself) and a recursive
  // expand toggles every descendant down the chain.
  const handleExpandGroup = useCallback(
    (aboId: string, recursive?: boolean) => {
      const changes = toggleExpand(aboId, recursive);
      // Only a recursive expand fires more than one onExpandChange call, so only that
      // case needs to invalidate a host's own queue of previously-mirrored calls first.
      if (recursive) {
        onExpandBatchStart?.(changes);
      }
      changes.forEach(({ aff, abo, expanded }) => {
        onExpandChange?.({ aff, abo }, expanded);
      });
    },
    [toggleExpand, onExpandChange, onExpandBatchStart],
  );

  const handleExpandAll = useCallback(() => {
    const changes = expandAll();
    onExpandBatchStart?.(changes);
    changes.forEach(({ aff, abo, expanded }) => {
      onExpandChange?.({ aff, abo }, expanded);
    });
  }, [expandAll, onExpandChange, onExpandBatchStart]);

  const handleCollapseAll = useCallback(() => {
    const changes = collapseToFrontline();
    onExpandBatchStart?.(changes);
    changes.forEach(({ aff, abo, expanded }) => {
      onExpandChange?.({ aff, abo }, expanded);
    });
  }, [collapseToFrontline, onExpandChange, onExpandBatchStart]);

  // Root's own node modal must agree with the toolbar's Expand All/Collapse All button -
  // both act on isExpandedBeyondFrontline, so route root's modal action through the same
  // expandAll/collapseToFrontline calls instead of the generic per-node toggleExpand used
  // for every other node's modal (and for the canvas's own +/- icon, left unchanged).
  const handleRootExpandGroup = useCallback(() => {
    if (isExpandedBeyondFrontline) {
      handleCollapseAll();
    } else {
      handleExpandAll();
    }
  }, [isExpandedBeyondFrontline, handleCollapseAll, handleExpandAll]);

  const handleModalExpandGroup = useCallback(
    (aboId: string, recursive?: boolean) => {
      if (aboData && aboId === aboData.abo.toString()) {
        handleRootExpandGroup();
        return;
      }
      handleExpandGroup(aboId, recursive);
    },
    [aboData, handleRootExpandGroup, handleExpandGroup],
  );

  const { allNodes } = useTreeViewSearch({
    aboData,
    expandPathToNode,
    setSelectedAbo,
    setSelectedNodeId,
    panToNode,
    initialSelectedAboNumber,
  });

  useCanvasRendering({
    canvasRef,
    paperInitializedRef,
    canvasSize,
    aboData,
    handleNodeSelect,
    // handleExpandGroup (not raw toggleExpand) so the canvas +/- icon's own click also
    // reports its change via onExpandChange - otherwise only the node modal's "Expand
    // Group" button synced to List View, and a direct icon click never did.
    toggleExpand: handleExpandGroup,
    translations,
    selectedNodeId,
    zoom,
    pan,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleWheelZoom,
    hideVolume,
  });

  const hasDownlines = aboData?.downlines && aboData.downlines.length > 0;

  const handleToggleWatchlist = useCallback(
    async (abo: TreeNodeData) => {
      const newWatchlistStatus = !isInWatchlist(abo);

      updateNodeWatchlist(abo.abo, abo.aff, newWatchlistStatus);
      setSelectedAbo((prev) => (prev ? { ...prev, watchlist: newWatchlistStatus } : prev));

      await toggleWatchlist(abo);
    },
    [toggleWatchlist, isInWatchlist, updateNodeWatchlist, setSelectedAbo],
  );

  const handleToggleFullscreen = useCallback(() => {
    toggleFullscreen(treeViewRootRef);
  }, [toggleFullscreen]);

  const handleApplyVolumeFilters = useCallback(
    (nextHideVolume: boolean, nextHideZeroVolumeAbos: boolean) => {
      setStoreState({ hideVolume: nextHideVolume, hideABOsZeroVolume: nextHideZeroVolumeAbos });
    },
    [setStoreState],
  );

  // Mirrors the wheel-zoom step factor in useCanvasInteractions so button-driven zoom
  // feels consistent with scroll/pinch zoom. setZoom (aka updateZoom) already clamps.
  const handleZoomIn = useCallback(() => setZoom(zoom * 1.1), [zoom, setZoom]);
  const handleZoomOut = useCallback(() => setZoom(zoom / 1.1), [zoom, setZoom]);
  // Reset restores the canvas to its literal default view - 100% zoom, root centered -
  // rather than re-fitting to content like the initial-load fit does.
  const handleResetView = useCallback(() => {
    resetZoom();
    resetPan();
  }, [resetZoom, resetPan]);

  return (
    <div className={`tree-view-poc ${isFullscreen ? "tree-view-poc--fullscreen" : ""}`} ref={treeViewRootRef}>
      <div className="tree-view-poc__canvas-container" ref={containerRef as React.RefObject<HTMLDivElement>}>
        {isFullscreen && (
          <TreeViewFullscreenControls
            allNodes={allNodes}
            inInternational={inInternational}
            hideInternationalDownlines={hideInternationalDownlines}
            hideVolume={hideVolume}
            hideZeroVolumeAbos={hideZeroVolumeAbos}
            onApplyVolumeFilters={handleApplyVolumeFilters}
            enableInternationalToggle={enableInternationalToggle}
            uiHeader={uiHeader}
            extraControls={fullscreenExtraControls}
            expandPathToNode={expandPathToNode}
            panToNode={panToNode}
            setSelectedAbo={setSelectedAbo}
            setSelectedNodeId={setSelectedNodeId}
          />
        )}
        {(isLoading || !aboData || !isFitReady) && !errorStatus && (
          <div className="tree-view-poc__loading">
            <div className="tree-view-poc__loading-spinner" />
            <span className="tree-view-poc__loading-text">{t("loading", defaultContent.loading)}</span>
          </div>
        )}
        {aboData && !errorStatus && !hasDownlines && !isLoading && (
          <div
            className={`tree-view-poc__no-downlines ${isFullscreen ? "tree-view-poc__no-downlines--fullscreen" : ""}`}
          >
            <span className="tree-view-poc__no-downlines--text">
              {t("noDownlinesInView", defaultContent.noDownlinesInView)}
            </span>
          </div>
        )}
        {errorStatus && (
          <div className="tree-view-poc__error">
            <span className="tree-view-poc__error-text">{t("errorLoadingData", defaultContent.errorLoadingData)}</span>
          </div>
        )}
        <canvas
          ref={canvasRef as React.RefObject<HTMLCanvasElement>}
          aria-label={String(
            t("accessibilityLabels.treeViewCanvas", defaultContent.accessibilityLabels.treeViewCanvas),
          )}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => {
            if (mouseDownOnCanvas && isDragging) handleMouseUp();
          }}
          style={{
            display: isLoading || !aboData || errorStatus || !hasDownlines || !isFitReady ? "none" : "block",
          }}
          className={`tree-view-poc__canvas ${
            isDragging ? "tree-view-poc__canvas--dragging" : "tree-view-poc__canvas--grabbable"
          }`}
        />
        <div className="tree-view-poc__zoom-controls">
          <TreeViewZoomControls
            zoomPercent={Math.round(zoom * 100)}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onReset={handleResetView}
            onFullscreenClick={handleToggleFullscreen}
            isFullscreen={isFullscreen}
            isExpandedBeyondFrontline={isExpandedBeyondFrontline}
            onExpandAll={handleExpandAll}
            onCollapseAll={handleCollapseAll}
          />
        </div>
      </div>
      {isFullscreen && <TreeViewFooter />}
      {selectedAbo && (
        <TreeViewNodeModal
          abo={selectedAbo}
          level={selectedAboLevel}
          onClose={() => setSelectedAbo(null)}
          onExpandGroup={handleModalExpandGroup}
          onToggleWatchlist={handleToggleWatchlist}
          isExpanded={isSelectedAboExpanded}
          canExpand={canSelectedAboExpand}
          canIsolate={canSelectedAboIsolate}
          config={config}
          externalLinks={externalLinks}
          hideExpandGroup={inInternational || !!selectedAbo?.internationalFlag}
          period={period}
          isIsolated={
            !!isolatedAbo &&
            isolatedAbo.aff === String(selectedAbo.aff).padStart(3, "0") &&
            isolatedAbo.abo === String(selectedAbo.abo)
          }
          onIsolateChange={
            onIsolateChange
              ? (isolating) => {
                  // Undoing Downline LOS keeps this TreeView mounted until its
                  // replacement data arrives. Collapse the isolated root first so
                  // its direct-downline row is not left visible during that handoff.
                  // isIsolated (required to reach this branch) confirms selectedAbo
                  // is the isolated tree's own root, so collapseToFrontline (the same
                  // action the modal/toolbar use for root) is the correct collapse here.
                  if (!isolating) {
                    // Tree data uses unpadded numeric affiliate IDs, unlike
                    // isolatedAboKey's API formatting (e.g. "10-123", not
                    // "010-123"). Store the same identity used by the merge.
                    pendingUnisolateRootKeyRef.current = `${selectedAbo.aff}-${selectedAbo.abo}`;
                    if (isSelectedAboExpanded) {
                      handleCollapseAll();
                    }
                  }
                  // selectedAbo.level reflects its position in the currently-fetched
                  // (possibly already-isolated) tree, not its true position in the
                  // full org - stamp on selectedAboLevel, which is already corrected
                  // for that, so the next isolate carries the right level forward.
                  onIsolateChange(isolating ? { ...selectedAbo, level: selectedAboLevel } : null);
                  setSelectedAbo(null);
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default TreeView;
